import React, {
  useState, useEffect, useMemo, forwardRef,
} from 'react';

import { SearchIcon } from '@vlabs/icons';
import type { ListsOption } from '@vlabs/monokit/features/create-face/types';
import type { Option } from '@vlabs/monokit/shared/ui/options-filter';
import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';
import type { FilterPopoverProps } from '@vlabs/monokit/shared/ui/popovers/filter-popover';
import { withPopover } from '@vlabs/monokit/shared/ui/popovers/withPopover';
import { SideMenuFilterItem } from '@vlabs/monokit/shared/ui/side-menu-filter-item';
import { Control } from '@vlabs/uikit';
import debounce from 'lodash/debounce';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { selectListOptions } from '@vlabs/shared/selectors/listSelectors';

import { useFilterCount } from '../hooks';
import st from './shared.module.sass';

interface ListsFilterPopoverProps extends FilterPopoverProps<string> {
  onCountChange?: (key: string, count: number) => void;
}

function ListsFilterPopover(
  { onCountChange = undefined, ...props }: ListsFilterPopoverProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const [listName, setListName] = useState<string | undefined>(undefined);
  const { watch, setValue } = useFormContext();

  const selectedList = watch('list_id');
  const filterCount = selectedList ? 1 : 0;
  useFilterCount('list_id', filterCount, onCountChange);

  const listsOptions = useSelector(selectListOptions) as ListsOption[];

  const filteredListOption = useMemo(() => {
    return listsOptions.filter((opt) => {
      if (!listName) return true;
      return opt.label.toLowerCase().includes(listName.toLowerCase());
    });
  }, [listsOptions, listName]);

  const debouncedSetListName = useMemo(() => debounce((value: string) => {
    setListName(value);
  }, 1000), []);

  useEffect(() => {
    return () => {
      debouncedSetListName.cancel();
    };
  }, [debouncedSetListName]);

  return (
    <PopoverWithButton
      {...props}
      onClickReset={() => setValue('list_id', '')}
      ref={ref}
    >
      <div className={st.Lists}>
        <Control.Input
          icon={<SearchIcon />}
          onChange={(e) => debouncedSetListName(e.target.value)}
        />
        <div className={st.RadioWrapper}>
          {filteredListOption.map((option: Option) => {
            return (
              <Control.Radio
                checked={watch('list_id') === option.value}
                key={option.value}
                label={option.label}
                onChange={() => setValue('list_id', option.value)}
              />
            );
          })}
        </div>
        <div className={st.Divider} />
      </div>
    </PopoverWithButton>
  );
}

const ListsFilterPopoverWithRef = forwardRef(ListsFilterPopover);

const withLeftPopover = withPopover({ placement: 'left' });

const withSideMenuItemLeftPopover = withLeftPopover(SideMenuFilterItem);

export const ListsFilterWithLeftPopover = withSideMenuItemLeftPopover(ListsFilterPopoverWithRef);
